import React, { useCallback, useState } from "react";
import styled from "styled-components";

import FilePicker from "../../../../app/_components/FilePicker";
import {
  Text,
  Button,
  Flex,
  Icon,
  Notice,
  pluralize,
  ItemType,
  utils,
  PortalItemType,
} from "@thenounproject/lingo-core";
import ModalBody from "../../../../app/_components/ModalBody";
import ModalHeader from "../../../../app/_components/ModalHeader";
import { getFileExtension, typeErrorMessage } from "@helpers/files";

import useModals, { ModalTypes } from "@redux/actions/useModals";
import useContentTypes from "@redux/actions/useFetchContentTypes";
import useDropboxPicker from "@hooks/useDropboxPicker";
import useGoogleDrivePicker from "@hooks/useGoogleDrivePicker";
import { Inspectable } from "@constants/Inspector";
import { InsertPosition } from "@redux/actions/uploads";
import { ExternalSource } from "@redux/actions/items/useCreateAssetsFromSource";

const DropZone = styled(Flex).attrs<{ hasHeader?: boolean }>(props => ({
  position: "relative",
  border: "grayDashed",
  borderRadius: "default",
  p: "xl",
  flexDirection: "column",
  alignItems: "center",
  mx: props.hasHeader ? undefined : "auto",
}))<{ hasHeader?: boolean }>``;

const FileError = styled(Notice).attrs({
  noticeStyle: "error",
  mt: "m",
  mb: "-m",
})`
  opacity: ${props => (props.message ? null : 0)};
`;

const ImportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border: 1px solid ${utils.getColor("grayLight")};
  border-radius: ${utils.getBorderRadius("default")};
  transition: background 0.2s ease;

  &:hover {
    background: ${utils.getColor("grayLightest")};
  }

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

type Props = {
  itemType?: ItemType | PortalItemType;
  assetType?: string;
  onUploadFiles: (files: File[]) => void;
  onImportFiles?: (files: ExternalSource[]) => void;
  prompt?: string;
  multiple?: boolean;
  // If replacing a file, inspectable and insert position are required to support figma import.
  // IDEA: Perhaps rework figma replacement to return a source instead of performing the replacement internally
  inspectable?: Inspectable;
  insertPosition?: InsertPosition;
};

const PickFileModal = ({
  onUploadFiles,
  onImportFiles,
  prompt,
  multiple = false,

  itemType: itemTypeProp,
  assetType: assetTypeProp,

  inspectable,
  insertPosition,
}: Props) => {
  const { dismissModal, showModal } = useModals();

  // Derive itemType and assetType from context
  const itemType = itemTypeProp ?? inspectable?.item?.type;
  const assetType = assetTypeProp ?? inspectable?.asset?.type;

  const showExternalSources = Boolean(onImportFiles);

  const { data: fileTypes, error: fileTypesError } = useContentTypes({
    itemType,
    assetType,
  });
  const openDropboxPicker = useDropboxPicker();
  const openGoogleDrivePicker = useGoogleDrivePicker();

  const [error, setError] = useState(""),
    fileExtensions = fileTypes?.reduce((acc, type) => {
      const extensions = [type.fileExtension, ...type.aliases].filter(t => t);
      return acc.concat(extensions);
    }, []);

  // Helper to build insertPosition from item context
  const getInsertPositionForContext = useCallback((): InsertPosition => {
    if (insertPosition) return insertPosition;
    if (inspectable?.item) {
      return {
        spaceId: inspectable?.item.spaceId,
        kitId: inspectable?.item.kitId,
        sectionId: inspectable?.item.sectionId,
      };
    }
    // This should never happen if the component is used correctly; fail fast if it does.
    throw new Error(
      "PickFileModal: getInsertPositionForContext requires either insertPosition or item to be provided"
    );
  }, [insertPosition, inspectable?.item]);

  const onPickFiles = useCallback(
    (files: File[]) => {
      function isValidFile(file: File) {
        const ext = getFileExtension(file.name);
        return fileExtensions.some((t: string) => t.toLowerCase() === ext);
      }
      // Stop the process if there are no valid files.
      // Currently this only checks for valid types
      if (fileTypes?.length && !files.some(isValidFile)) {
        return setError(typeErrorMessage(fileTypes));
      }
      dismissModal();
      return onUploadFiles(files);
    },
    [dismissModal, fileExtensions, fileTypes, onUploadFiles]
  );

  // Shared handler for external source selection
  const handleExternalSourcePick = useCallback(
    (sources: ExternalSource[]) => {
      if (sources.length === 0) return;

      if (!multiple && sources.length > 1) {
        return setError("Please select a single file.");
      }
      dismissModal();
      onImportFiles(sources);
    },
    [dismissModal, multiple, onImportFiles]
  );

  const handleDropboxPick = useCallback(() => {
    openDropboxPicker({
      multiselect: multiple,
      onSuccess: handleExternalSourcePick,
    });
  }, [openDropboxPicker, multiple, handleExternalSourcePick]);

  const handleGoogleDrivePick = useCallback(() => {
    openGoogleDrivePicker({
      multiselect: multiple,
      onSuccess: handleExternalSourcePick,
    });
    dismissModal();
  }, [openGoogleDrivePicker, multiple, handleExternalSourcePick, dismissModal]);

  const handleFigmaPick = useCallback(() => {
    dismissModal();
    showModal(ModalTypes.FIGMA_LINK_MODAL, {
      insertPosition: getInsertPositionForContext(),
      inspectable,
    });
  }, [inspectable, getInsertPositionForContext, dismissModal, showModal]);

  const importSources = [
    inspectable && {
      id: "figma",
      title: "Figma",
      icon: "integrations.figma",
      onClick: handleFigmaPick,
    },
    {
      id: "google-drive",
      title: "Google Drive",
      icon: "integrations.google-drive",
      onClick: handleGoogleDrivePick,
    },
    {
      id: "dropbox",
      title: "Dropbox",
      icon: "integrations.dropbox",
      onClick: handleDropboxPick,
    },
  ].filter(Boolean);

  const modalTitle = inspectable?.asset ? "Replace file" : "Upload file";

  return (
    <>
      {showExternalSources && <ModalHeader title={modalTitle} />}
      <ModalBody pt={showExternalSources ? "l" : "xxl"} pb="xl">
        <FilePicker onPickFiles={onPickFiles} fileExtensions={fileExtensions} multiple={multiple}>
          {({ openNativeFilePicker, openNativeFolderPicker, dropZoneProps }) => {
            return (
              <DropZone hasHeader={showExternalSources} {...dropZoneProps}>
                <Icon iconId="empty-state.assets" fill="gray" size="120" flex="1 1 auto" />
                <Text>
                  Drag & drop your new {pluralize("file", multiple ? 0 : 1)} here or click below
                </Text>
                <Button
                  mt="m"
                  flex="0 0 auto"
                  text={prompt || pluralize("Choose file", multiple ? 0 : 1)}
                  onClick={openNativeFilePicker}
                />
                {multiple && (
                  <Button
                    mt="m"
                    buttonStyle="tertiary"
                    flex="0 0 auto"
                    text="Choose a folder"
                    size="small"
                    onClick={openNativeFolderPicker}
                  />
                )}
              </DropZone>
            );
          }}
        </FilePicker>
        {error || fileTypesError ? <FileError message={error || fileTypesError?.message} /> : null}

        {showExternalSources && (
          <Flex flexDirection="column" mt="m">
            <Text font="ui.smallBold" color="grayDarkest" mb="m">
              Import from
            </Text>
            {importSources.map(source => (
              <ImportButton key={source.id} onClick={source.onClick}>
                <Flex alignItems="center" gap="16px">
                  <Flex
                    height="40px"
                    width="40px"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="grayLightest"
                    borderRadius="4px">
                    <Icon iconId={source.icon} size="24" />
                  </Flex>
                  <Text font="ui.regular">{source.title}</Text>
                </Flex>
                <Icon iconId="navigation.chevron-right" size="25" />
              </ImportButton>
            ))}
          </Flex>
        )}
      </ModalBody>
    </>
  );
};

export default PickFileModal;
